/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useDispatch } from 'react-redux';

import './Css/Style.css';
import './index.css';
import './Css/NavBarElement.css';
import './Css/PricingDetail.css';
import './Css/DataTable.css';
import './Css/Scrollbar.css';
import './Css/Button.css';
import './Css/Link.css';
import './Css/OverWindow.css';
import './Css/ProgressBarDots.css';
import './Css/Input.css';

import NavBar from './MenuElements/BravoSidebar.tsx';
import ErrorMessage from './Elements/ErrorMessage.js';
import NotificationPopupHost from './Elements/NotificationPopupHost.tsx';
import { MessagePopupTrigger, DownloadPopupTrigger } from './Elements/NotificationTriggers.tsx';
import AuthService from './Services/authService.js';
import OfficeService from './Services/OfficeService.js';
import BravoLoader from './Elements/BravoLoader';
import CommandPalette from './Elements/CommandPalette/CommandPalette.tsx';
import SignInUnavailable from './Elements/SignInUnavailable.tsx';
import { signIn } from './Services/msalClient';
import { palette_set_open } from './redux/actions';

const App = () => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [loggedInUserNotInUse, setLoggedInUserNotInUse] = useState(null);
  const [signInBlockedReason, setSignInBlockedReason] = useState(null);
  const [officesLoaded, setOfficesLoaded] = useState(false);
  const authService = React.useMemo(() => {
    return isAuthenticated ? AuthService.getInstance() : null;
  }, [isAuthenticated]);

  // If not authenticated and MSAL is idle, redirect to Microsoft login
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      signIn();
    }
  }, [isAuthenticated, inProgress]);

  // Global record search (CD-5425). Ctrl/Cmd-K is the primary entry point and was confirmed free:
  // no other ctrlKey/metaKey handler exists in app.js or MenuElements. Registered once at app level
  // so it works on every page.
  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
        // Take the shortcut back from the browser's own address-bar search.
        event.preventDefault();
        dispatch(palette_set_open(true));
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dispatch]);

  const handleLogoutClick = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
      mainWindowRedirectUri: window.location.origin,
    });
  };
  const handleSwitchToUserClick = (user) => {
    authService?.switchToUser(user);
  };

  // Reflect the AuthService's resolved user into local state. We subscribe to
  // its user-resolved event (fired when Login() completes) instead of polling,
  // and apply immediately in case login resolved before this effect ran.
  useEffect(() => {
    if (!authService) return undefined;

    const applyUser = () => {
      setLoggedInUser(authService.GetLoggedInUser());
      setLoggedInUserNotInUse(authService.GetLoggedInUsersNotInUse());
      setSignInBlockedReason(authService.GetSignInBlockedReason());
    };

    applyUser();
    authService.listenUserResolved(applyUser);

    // Safety net: if the user never resolves (e.g. the initial /api/User call
    // failed outright), reload once to retry. Guarded so it can't fire after a
    // successful login, and cleared on unmount. A blocked sign-in is a settled
    // answer rather than a stalled one, so it opts out of the retry (CD-5515).
    const fallbackTimer = setTimeout(() => {
      if (!authService.GetLoggedInUser() && !authService.GetSignInBlockedReason()) {
        console.log('Logged-in user did not resolve; reloading to retry...');
        window.location.reload();
      }
    }, 30000);

    return () => {
      authService.unlistenUserResolved(applyUser);
      clearTimeout(fallbackTimer);
    };
  }, [authService]);

  // OfficeHelper reads the cached office list synchronously (office groups), so the app waits for it alongside the
  // user. A failed load does not block the app; the next office list request retries it.
  useEffect(() => {
    if (!authService) return;

    OfficeService.GetOfficesForSelect()
      .catch(() => console.log('Office list did not load; it is retried on the next request.'))
      .finally(() => setOfficesLoaded(true));
  }, [authService]);

  let content = '';
  if (!isAuthenticated) {
    content = (
      <BravoLoader
        loading
        variant='fullscreen'
        subtitle={'Bravo is loading...'}
      />
    );
  } else {
    if (loggedInUser && officesLoaded) {
      content = (
        <div>
          <ErrorMessage />
          <NotificationPopupHost />
          <MessagePopupTrigger />
          <DownloadPopupTrigger />
          <NavBar
            activenavbutton={0}
            activesubmenubutton={0}
            loggedInUser={loggedInUser}
            loggedInUsersNotInUse={loggedInUserNotInUse}
            handleLogoutClick={handleLogoutClick}
            switchToUserClick={handleSwitchToUserClick}
            handleSwitchUserClick={() => {
              authService.ClearCache();
            }}
          />
          <CommandPalette />
        </div>
      );
    } else if (signInBlockedReason) {
      content = (
        <SignInUnavailable
          reason={signInBlockedReason}
          accountName={instance.getActiveAccount()?.username ?? accounts[0]?.username ?? ''}
          onTryDifferentAccount={() => {
            authService.ClearCache();
          }}
        />
      );
    } else {
      content = (
        <div>
          <BravoLoader
            loading
            variant='fullscreen'
            subtitle={'Authenticating...'}
          />
          <ErrorMessage />
        </div>
      );
    }
  }

  return <div className='arial'>{content}</div>;
};

export default App;
